import { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { usePossibleUser } from '@/hooks/useProfile';

import settingsRoutes from '@/pages/Settings/settings.routes';
import { safeLazy } from '@/components/SafeLazy/SafeLazy';

const Manual = safeLazy(() => import('@/pages/Manual/Manual'));

const UsersManagement = safeLazy(
  () => import('@/components/Admin/Dashboard/Pages/UsersManagement/UsersManagement')
);

const CompaniesManagement = safeLazy(
  () => import('@/components/Admin/Dashboard/Pages/CompaniesManagement/CompaniesManagement')
);

const CompanyPageEdit = safeLazy(() => import('@/pages/CompanyPage/Company/Editor'));

const Products = safeLazy(() => import('@/pages/Products/Products'));

const ProductPageEdit = safeLazy(() => import('@/pages/CompanyPage/Product/Editor'));

const Tiers = safeLazy(() => import('@/components/Dashboard/Pages/Payments/Tiers'));

const GalleryManager = safeLazy(
  () => import('@/components/Dashboard/Pages/GalleryManager/GalleryManager')
);

const ApplyQRCode = safeLazy(
  () => import('@/components/Dashboard/Pages/Services/Document/QRCode/ApplyQRCode')
);

const VerifyDocument = safeLazy(
  () => import('@/components/Dashboard/Pages/Services/Document/VerifyDocuments/VerifyDocument')
);
const InsertDocument = safeLazy(
  () => import('@/components/Dashboard/Pages/Services/Document/InsertDocument')
);

const TraceData = safeLazy(() => import('@/components/Dashboard/Pages/Services/Trace/TraceData'));
const EditTraceData = safeLazy(
  () => import('@/components/Dashboard/Pages/Services/Trace/TraceDataEditor')
);

const Statistics = safeLazy(() => import('@/pages/Statistics/Statistics'));

const ChainInsert = safeLazy(() => import('@/components/Common/ChainManager/Insert'));
const QRCodeCreate = safeLazy(
  () => import('@/components/Dashboard/Pages/Services/QRCodeManager/Management/Management')
);

const HistoryPage = safeLazy(() => import('@/pages/History/History'));
const OrdersPage = safeLazy(() => import('@/pages/Orders/Orders'));

const ManageChain = safeLazy(() => import('@/components/Common/ChainManager/List'));
const Chain = safeLazy(() => import('@/components/Dashboard/ContextPages/Chain/Chain'));

export const PrivateRoutes = () => {
  const user = usePossibleUser();
  const hasQrCode = usePermissions('manage_qrcode');
  const hasTrace = usePermissions('trace');
  const hasDocument = usePermissions('document');
  const hasApi = usePermissions('manage_api');
  const hasDocumentQrCode = usePermissions('document_qrcode');
  const canManageData = usePermissions('manage_data');
  const hasStats = usePermissions('stats');
  const hasMarketplace = usePermissions('marketplace');

  const isAdmin = user?.role === 'admin';
  const isIndustryAdmin = usePermissions('admin');
  const role = user?.role;

  const settingsRouter = useMemo(
    () =>
      settingsRoutes({
        industry: isIndustryAdmin,
        admin: isAdmin
      }),
    [isIndustryAdmin, isAdmin]
  );

  /* {
    $features.qrcodeEditor && <Route path="/qrcode-editor" key="/qrcode-editor" element={<QRCodeEditor />} />
  }*/

  const userRoutes = useMemo(
    () => [
      <Route path="/verify-document" key="/verify-document" element={<VerifyDocument />} />,
      <Route key="/company/own/edit" path="/company/own/edit" element={<CompanyPageEdit />} />,
      <Route key="/products" path="/products" element={<Products />} />,
      <Route key="/products/:id/edit" path="/products/:id/edit" element={<ProductPageEdit />} />,

      hasDocument && [
        <Route path="/add-document" key="/add-document" element={<InsertDocument />} />
      ],

      hasDocumentQrCode && (
        <Route path="/apply-qr-code/:id/" key="/apply-qr-code/:id/" element={<ApplyQRCode />} />
      ),

      hasQrCode && [
        <Route path="/qrcode-create" key="/qrcode-create" element={<QRCodeCreate />} />,
        <Route path="/qrcode-create/:page" key="/qrcode-create/:page" element={<QRCodeCreate />} />
      ],

      hasMarketplace && [<Route path="/orders" key="/orders" element={<OrdersPage />} />],

      hasTrace && [
        <Route path="/gallery/" key="/gallery/" element={<GalleryManager />} />,
        <Route path="/add-trace-item" key="/add-trace-item" element={<TraceData />} />,
        <Route path="/edit/trace/:id" key="/edit/trace/:id" element={<EditTraceData />} />
      ],

      (canManageData || role === 'industry') && [
        <Route path="/history/:tab/:page" key="/history" element={<HistoryPage />} />
      ],

      (hasStats || role === 'industry') && [
        <Route path="/statistics" key="/stats" element={<Statistics />} />,
        <Route path="/statistics/:page" key="/stats/:page" element={<Statistics />} />
      ],

      settingsRouter,

      <Route path="/manual/:chapter" key="/manual/:chapter" element={<Manual />} />,

      role === 'industry' && [
        <Route
          path="pricing/document"
          key="pricing/document"
          element={<Tiers type="document" />}
        />,
        <Route
          path="pricing/traceability"
          key="pricing/traceability"
          element={<Tiers type="trace" />}
        />,
        <Route path="pricing/more" key="pricing/more" element={<Tiers type="one-time" />} />
      ]
    ],
    [user, hasQrCode, hasTrace, hasApi, hasDocument]
  );

  const adminRoutes = useMemo(
    () => [
      <Route path="/companies/manage" key="/companies/manage" element={<CompaniesManagement />} />,
      <Route path="/users-management" key="/users-management" element={<UsersManagement />} />,
      <Route path="/statistics" key="/stats" element={<Statistics />} />,
      <Route path="/statistics/:page" key="/stats/:page" element={<Statistics />} />,
      settingsRouter
    ],
    []
  );

  const memoizedRoutes = useMemo(
    () =>
      (isAdmin || role === 'read_only_admin' ? adminRoutes : userRoutes)
        .filter(Boolean)
        .flatMap((it) => (Array.isArray(it) ? it : [it])),
    [user]
  );

  return (
    <Routes>
      <Route path="*" key="*" element={<Navigate to="/404" />} />
      <Route path="/edit/chain/:id" key="/edit/chain/:id" element={<ChainInsert />} />
      <Route path="/manage-chain/:page" key="/manage-chain/:page" element={<ManageChain />} />
      <Route path="/manage-chain" key="/manage-chain" element={<ManageChain />} />
      <Route path="/chain/:id/" key="/chain/:id/" element={<Chain />} />
      {memoizedRoutes}
    </Routes>
  );
};
